import { localizeFitnessProductExerciseName } from '@/lib/fitnessProductExerciseLabels';
// Fitness API layer — ExerciseDB integration with fallback seed data

export const FITNESS_EXERCISEDB_ENABLED = true;
export const APPLE_HEALTH_ENABLED = false;
export const GOOGLE_FIT_ENABLED = false;
export const GARMIN_ENABLED = false;
export const WHOOP_ENABLED = false;

const EXERCISEDB_BASE = "https://exercisedb.p.rapidapi.com";
const CACHE_KEY = "vyv_exercises_cache:v2:";
const CATALOG_TIMEOUT_MS = 15_000; // Same bounded wait as the exercise demonstration loader.
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h
// Only the newest HTTP request for a catalog may refresh its shared cache.
const catalogRequestGeneration = new Map<string, number>();
const catalogKey = (bodyPart?: string) => bodyPart?.toLowerCase() || "all";

export interface Exercise {
  id: string;
  name: string;
  bodyPart: string;
  equipment: string;
  gifUrl: string;
  target: string;
  secondaryMuscles: string[];
  instructions: string[];
}

// Bundled fallback catalog; existing seed IDs and order stay stable.
const SEED_EXERCISES: Exercise[] = [
  { id: "s1", name: "Push Up", bodyPart: "chest", equipment: "body weight", gifUrl: "", target: "pectorals", secondaryMuscles: ["triceps", "deltoids"], instructions: ["Start in plank position", "Lower chest to floor", "Push back up"] },
  { id: "s2", name: "Pull Up", bodyPart: "back", equipment: "body weight", gifUrl: "", target: "lats", secondaryMuscles: ["biceps", "forearms"], instructions: ["Hang from bar", "Pull chin above bar", "Lower slowly"] },
  { id: "s3", name: "Squat", bodyPart: "upper legs", equipment: "body weight", gifUrl: "", target: "quads", secondaryMuscles: ["glutes", "hamstrings"], instructions: ["Stand feet shoulder width", "Lower hips back", "Drive up through heels"] },
  { id: "s4", name: "Deadlift", bodyPart: "back", equipment: "barbell", gifUrl: "", target: "glutes", secondaryMuscles: ["quads", "hamstrings", "back"], instructions: ["Stand over bar", "Grip and lift with flat back", "Lock out hips"] },
  { id: "s5", name: "Bench Press", bodyPart: "chest", equipment: "barbell", gifUrl: "", target: "pectorals", secondaryMuscles: ["triceps", "deltoids"], instructions: ["Lie on bench", "Lower bar to chest", "Press up"] },
  { id: "s6", name: "Overhead Press", bodyPart: "shoulders", equipment: "barbell", gifUrl: "", target: "deltoids", secondaryMuscles: ["triceps", "upper chest"], instructions: ["Stand with bar at shoulders", "Press overhead", "Lower controlled"] },
  { id: "s7", name: "Barbell Row", bodyPart: "back", equipment: "barbell", gifUrl: "", target: "upper back", secondaryMuscles: ["biceps", "lats"], instructions: ["Hinge at hips", "Pull bar toward lower ribs", "Squeeze shoulder blades"] },
  { id: "s8", name: "Dumbbell Curl", bodyPart: "upper arms", equipment: "dumbbell", gifUrl: "", target: "biceps", secondaryMuscles: ["forearms"], instructions: ["Stand with dumbbells", "Curl to shoulders", "Lower slowly"] },
  { id: "s9", name: "Tricep Dip", bodyPart: "upper arms", equipment: "body weight", gifUrl: "", target: "triceps", secondaryMuscles: ["chest", "deltoids"], instructions: ["Grip parallel bars", "Lower body", "Press back up"] },
  { id: "s10", name: "Lateral Raise", bodyPart: "shoulders", equipment: "dumbbell", gifUrl: "", target: "deltoids", secondaryMuscles: [], instructions: ["Stand with dumbbells at sides", "Raise arms to shoulder height", "Lower slowly"] },
  { id: "s11", name: "Leg Press", bodyPart: "upper legs", equipment: "leverage machine", gifUrl: "", target: "quads", secondaryMuscles: ["glutes", "hamstrings"], instructions: ["Sit in machine", "Press platform away", "Lower with control"] },
  { id: "s12", name: "Lunges", bodyPart: "upper legs", equipment: "body weight", gifUrl: "", target: "quads", secondaryMuscles: ["glutes", "hamstrings"], instructions: ["Step forward", "Lower back knee", "Push back to start"] },
  { id: "s13", name: "Plank", bodyPart: "waist", equipment: "body weight", gifUrl: "", target: "abs", secondaryMuscles: ["shoulders", "back"], instructions: ["Forearms on floor", "Body straight", "Hold position"] },
  { id: "s14", name: "Crunches", bodyPart: "waist", equipment: "body weight", gifUrl: "", target: "abs", secondaryMuscles: [], instructions: ["Lie on back", "Curl shoulders up", "Lower controlled"] },
  { id: "s15", name: "Romanian Deadlift", bodyPart: "upper legs", equipment: "barbell", gifUrl: "", target: "hamstrings", secondaryMuscles: ["glutes", "lower back"], instructions: ["Hold bar", "Hinge at hips", "Feel hamstring stretch"] },
  { id: "s16", name: "Calf Raise", bodyPart: "lower legs", equipment: "body weight", gifUrl: "", target: "calves", secondaryMuscles: [], instructions: ["Stand tall with both feet on the floor", "Rise onto the balls of both feet", "Lower heels to the floor with control"] },
  { id: "s17", name: "Face Pull", bodyPart: "shoulders", equipment: "cable", gifUrl: "", target: "rear deltoids", secondaryMuscles: ["upper back", "rotator cuff"], instructions: ["Set cable high", "Pull to face", "Squeeze shoulder blades"] },
  { id: "s18", name: "Dumbbell Fly", bodyPart: "chest", equipment: "dumbbell", gifUrl: "", target: "pectorals", secondaryMuscles: ["deltoids"], instructions: ["Lie on bench with dumbbells", "Open arms wide", "Squeeze together"] },
  { id: "s19", name: "Leg Curl", bodyPart: "upper legs", equipment: "leverage machine", gifUrl: "", target: "hamstrings", secondaryMuscles: ["calves"], instructions: ["Lie face down on machine", "Curl heels to glutes", "Lower slowly"] },
  { id: "s20", name: "Cable Crunch", bodyPart: "waist", equipment: "cable", gifUrl: "", target: "abs", secondaryMuscles: [], instructions: ["Kneel at cable", "Crunch down", "Return controlled"] },
  { id: "s21", name: "Jumping Jacks", bodyPart: "cardio", equipment: "body weight", gifUrl: "", target: "cardiovascular system", secondaryMuscles: ["calves", "quads", "deltoids"], instructions: ["Stand with feet together and arms at your sides", "Jump feet apart while raising arms overhead", "Land softly and return to the starting position"] },
];

function validExercises(data: unknown): data is Exercise[] {
  return Array.isArray(data) && data.every(ex => ex && ["id", "name", "bodyPart", "equipment", "gifUrl", "target"].every(key => typeof ex[key] === "string") && Array.isArray(ex.secondaryMuscles) && ex.secondaryMuscles.every((v: unknown) => typeof v === "string") && Array.isArray(ex.instructions) && ex.instructions.every((v: unknown) => typeof v === "string"));
}

// Recognize only the complete original bundled catalog. A provider/partial
// catalog keeps its own IDs, values and order. Reads preserve the stored TTL.
const LEGACY_SEED_COUNT = 20;
function supplementLegacySeedCatalog(data: Exercise[], bodyPart?: string): Exercise[] {
  if (catalogKey(bodyPart) !== "all" || ![LEGACY_SEED_COUNT, SEED_EXERCISES.length].includes(data.length) ||
    !data.every((exercise, index) => exercise.id === SEED_EXERCISES[index].id && exercise.name === SEED_EXERCISES[index].name && exercise.gifUrl === "")) return data;
  const corrected = data.map(exercise => {
    const seed = SEED_EXERCISES.find(item => item.id === exercise.id)!;
    if (exercise.id === "s4" && exercise.target === "spine") return { ...exercise, target: seed.target, secondaryMuscles: [...seed.secondaryMuscles] };
    if (exercise.id === "s7" && JSON.stringify(exercise.instructions) === JSON.stringify(["Hinge at hips", "Pull bar to lower chest", "Squeeze shoulder blades"])) return { ...exercise, instructions: [...seed.instructions] };
    if (exercise.id === "s16" && JSON.stringify(exercise.instructions) === JSON.stringify(["Stand on edge of step", "Rise on toes", "Lower heels below step"])) return { ...exercise, instructions: [...seed.instructions] };
    return exercise;
  });
  return corrected.length === LEGACY_SEED_COUNT ? [...corrected, ...SEED_EXERCISES.slice(LEGACY_SEED_COUNT)] : corrected;
}

function getCached(bodyPart?: string): { data: Exercise[]; ts: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY + catalogKey(bodyPart));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed.ts) || parsed.ts > Date.now() || Date.now() - parsed.ts > CACHE_TTL || !validExercises(parsed.data)) return null;
    return { data: supplementLegacySeedCatalog(parsed.data, bodyPart), ts: parsed.ts };
  } catch { return null; }
}

function setCache(data: Exercise[], bodyPart: string | undefined, generation: number) {
  if (catalogRequestGeneration.get(catalogKey(bodyPart)) !== generation) return;
  try {
    localStorage.setItem(CACHE_KEY + catalogKey(bodyPart), JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // The catalog stays usable when storage is full or unavailable.
  }
}

export async function fetchExercises(
  bodyPart?: string,
  equipment?: string,
  limit = 20
): Promise<Exercise[]> {
  // Check cache first
  const cached = getCached(bodyPart);
  if (cached) {
    let filtered = cached.data;
    if (bodyPart) filtered = filtered.filter(e => e.bodyPart.toLowerCase() === bodyPart.toLowerCase() || e.target.toLowerCase() === bodyPart.toLowerCase());
    if (equipment) filtered = filtered.filter(e => e.equipment.toLowerCase().includes(equipment.toLowerCase()));
    return filtered.slice(0, limit);
  }

  if (!FITNESS_EXERCISEDB_ENABLED) return filterSeed(bodyPart, equipment, limit);

  const key = catalogKey(bodyPart);
  const generation = (catalogRequestGeneration.get(key) || 0) + 1;
  catalogRequestGeneration.set(key, generation);

  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error("Exercise catalog timed out"));
    }, CATALOG_TIMEOUT_MS);
  });
  try {
    const requestCatalog = async () => {
      const url = bodyPart
        ? `${EXERCISEDB_BASE}/exercises/bodyPart/${encodeURIComponent(bodyPart)}?limit=100`
        : `${EXERCISEDB_BASE}/exercises?limit=200`;
      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          "X-RapidAPI-Key": import.meta.env.VITE_RAPIDAPI_KEY || "",
          "X-RapidAPI-Host": "exercisedb.p.rapidapi.com",
        },
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const result: unknown = await res.json();
      if (!validExercises(result)) throw new Error("Invalid exercise catalog");
      return result;
    };
    // The deadline also covers response-body decoding and a fetch implementation
    // that ignores AbortSignal. Late responses cannot publish a cache afterward.
    const data = await Promise.race([requestCatalog(), deadline]);
    setCache(data, bodyPart, generation);

    let filtered = data;
    if (equipment) filtered = filtered.filter(e => e.equipment.toLowerCase().includes(equipment.toLowerCase()));
    return filtered.slice(0, limit);
  } catch {
    return filterSeed(bodyPart, equipment, limit);
  } finally {
    clearTimeout(timer!);
  }
}

function filterSeed(bodyPart?: string, equipment?: string, limit = 20): Exercise[] {
  let filtered = SEED_EXERCISES;
  if (bodyPart) filtered = filtered.filter(e => e.bodyPart === bodyPart || e.target.includes(bodyPart));
  if (equipment) filtered = filtered.filter(e => e.equipment.includes(equipment));
  return filtered.slice(0, limit);
}

/** Resolve the exact catalog item used by the library, including offline seeds.
 * This only reads validated, fresh v2 public catalog caches. It never accesses
 * authenticated workout data or depends on the retired unversioned cache.
 */
function findCatalogExercise(matches: (exercise: Exercise) => boolean): Exercise | null {
  const all = getCached()?.data.find(matches);
  if (all) return all;
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(CACHE_KEY) && key !== CACHE_KEY + "all") keys.push(key);
    }
    for (const key of keys.sort()) {
      const found = getCached(key.slice(CACHE_KEY.length))?.data.find(matches);
      if (found) return found;
    }
  } catch {
    // Storage can be unavailable; the same offline seed catalog still resolves.
  }
  return SEED_EXERCISES.find(matches) ?? null;
}

export function getExerciseById(exerciseId: string): Exercise | null {
  const id = exerciseId.trim();
  return id ? findCatalogExercise(exercise => exercise.id === id) : null;
}

/** Exact normalized name lookup for a workout item; never guesses a movement. */
export function getExerciseByName(exerciseName: string): Exercise | null {
  const name = exerciseName.trim().toLowerCase();
  return name ? findCatalogExercise(exercise => exercise.name.trim().toLowerCase() === name) : null;
}

// ── Exercise name localisation ──────────────────────────────────────────────
// Maps common English exercise names (lowercase) to i18n keys.
// If the name isn't in the map the original string is returned as-is.

const EXERCISE_NAME_MAP: Record<string, string> = {
  "push up": "fitness.exercise.pushUp",
  "pull up": "fitness.exercise.pullUp",
  "squat": "fitness.exercise.squat",
  "deadlift": "fitness.exercise.deadlift",
  "bench press": "fitness.exercise.benchPress",
  "overhead press": "fitness.exercise.overheadPress",
  "barbell row": "fitness.exercise.barbellRow",
  "dumbbell curl": "fitness.exercise.dumbbellCurl",
  "tricep dip": "fitness.exercise.tricepDip",
  "lateral raise": "fitness.exercise.lateralRaises",
  "lateral raises": "fitness.exercise.lateralRaises",
  "leg press": "fitness.exercise.legPress",
  "lunges": "fitness.exercise.lunges",
  "plank": "fitness.exercise.plank",
  "crunches": "fitness.exercise.crunches",
  "romanian deadlift": "fitness.exercise.romanianDeadlift",
  "calf raise": "fitness.exercise.calfRaise",
  "calf raises": "fitness.exercise.calfRaise",
  "face pull": "fitness.exercise.facePull",
  "dumbbell fly": "fitness.exercise.dumbbellFly",
  "dumbbell flyes": "fitness.exercise.dumbbellFly",
  "leg curl": "fitness.exercise.legCurl",
  "cable crunch": "fitness.exercise.cableCrunch",
  "jumping jack": "fitness.exercise.jumpingJacks",
  "jumping jacks": "fitness.exercise.jumpingJacks",
  "incline dumbbell press": "fitness.exercise.inclineDumbbellPress",
  "cable fly": "fitness.exercise.cableFlyes",
  "cable flyes": "fitness.exercise.cableFlyes",
};

export function localizeExerciseName(name: string, t: (key: string) => string, lang?: string): string {
  const productLabel = localizeFitnessProductExerciseName(name, lang);
  if (productLabel) return productLabel;
  const key = EXERCISE_NAME_MAP[name.toLowerCase().trim()];
  return key ? t(key) : name;
}

export function getExercisesByMuscle(muscle: string): Exercise[] {
  const cached = getCached();
  const pool = cached?.data || SEED_EXERCISES;
  return pool.filter(e =>
    e.target.toLowerCase().includes(muscle.toLowerCase()) ||
    e.bodyPart.toLowerCase().includes(muscle.toLowerCase()) ||
    e.secondaryMuscles.some(m => m.toLowerCase().includes(muscle.toLowerCase()))
  );
}
